import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { requestManager } from '../services/requestManager';
import { apiLogger } from '../utils/logger';

// Use environment variable for API base URL, fallback to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';
export const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';
export const API_PREFIX = `/api/${API_VERSION}`;

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Version': API_VERSION,
  },
});

// Request interceptor - add auth token and monitor circuit breaker (non-blocking)
apiClient.interceptors.request.use(config => {
  // Circuit breaker is now monitor-only - warn but don't block
  if (!requestManager.shouldAllowRequest()) {
    apiLogger.warn('[API Client] Circuit breaker warning - backend may be having issues:', config.url);
    // Don't block - just log warning and continue
  }

  const token = localStorage.getItem('hyb_dashboard_token');
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - track success/failure for circuit breaker
apiClient.interceptors.response.use(
  response => {
    // Record success for circuit breaker
    requestManager.recordSuccess();
    return response;
  },
  (error: AxiosError) => {
    // Check if request was cancelled by circuit breaker
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Network errors or 5xx errors indicate backend issues
    const isNetworkError = !error.response;
    const isServerError = error.response?.status && error.response.status >= 500;

    if (isNetworkError || isServerError) {
      requestManager.recordFailure();
    } else {
      // 4xx errors are client errors, backend is healthy
      requestManager.recordSuccess();
    }

    // Handle 401 Unauthorized (token expired)
    if (error.response?.status === 401) {
      const errorCode = (error.response?.data as any)?.error?.code;
      const errorMessage = (error.response?.data as any)?.error?.message || '';

      // Check if it's an authentication error
      if (errorCode === 'UNAUTHORIZED' ||
        errorMessage.toLowerCase().includes('token') ||
        errorMessage.toLowerCase().includes('expired')) {
        toast.error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง', {
          duration: 3000,
        });

        // Clear authentication data
        localStorage.removeItem('hyb_dashboard_token');
        localStorage.removeItem('hyb_dashboard_user');

        // Redirect to login page after a short delay
        setTimeout(() => {
          window.location.href = '/login?reason=session_expired';
        }, 500);
      }
    }

    // Handle rate limit (429) - show toast warning
    if (error.response?.status === 429) {
      apiLogger.warn('[API Client] Rate limit hit (429)');
      toast.error('คำขอมากเกินไป กรุณารอสักครู่', {
        duration: 3000,
        id: 'rate-limit-warning',
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;

